/* eslint-disable @typescript-eslint/no-explicit-any */
import type { CreateBillBody, Item } from '@/types/backend'
import type { CartDraft, CartItem } from '@/types/cart'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function subtotal(items: CartItem[]) {
  return items.reduce((a, i) => a + i.unitPrice * i.quantity, 0)
}

export function totals(draft: CartDraft) {
  const sub = subtotal(draft.items)
  const t = draft.tax ?? 0
  return { subtotal: sub, tax: t, total: sub + t }
}

export function toCreateBillDto(d: CartDraft): CreateBillBody {
  if (!d.customerId) throw new Error('customerId requis')

  return {
    customerId: d.customerId,
    initialOverpayPolicy: d.initialOverpayPolicy ?? 'RETURN',
    items: d.items.map((i) => ({
      name: i.name,
      unitPrice: i.unitPrice,
      quantity: i.quantity,
      category: i.category,
      packaging: i.packaging,
      itemId: i.itemId,
    })),
    tax: d.tax,
    incurredOn: d.incurredOn,
    notes: d.notes,
    initialPaymentAmount: d.initialPaymentAmount,
    initialPaymentMethod: d.initialPaymentAmount as any,
  }
}

export function itemToCartItem(item: Item, quantity: number): CartItem {
  return {
    id: item.id,
    sku: item.sku || '',
    itemId: item.id,
    name: item.name,
    unitPrice: Number(item.unitPrice),
    quantity,
    category: item.category?.name ?? undefined,
    packaging: item.packaging ?? undefined,
  }
}

export function currentQtyInCart(cart: CartDraft, itemId: string): number {
  return cart.items.find((item) => item.itemId === itemId)?.quantity ?? 0
}

export function formatCFA(
  value: number | null | undefined,
  options: Intl.NumberFormatOptions = {}
): string {
  if (value == null || Number.isNaN(value)) return ''
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0,
    ...options,
  }).format(value)
}

export function formatDateFR(
  input?: string | number | Date,
  options?: {
    showTime?: 'auto' | 'always' | 'never' // auto = date+heure si heure non nulle
    withSeconds?: boolean // inclut les secondes si true
    timeZone?: string // ex. 'UTC'
  }
) {
  if (!input) return '—'
  const d = new Date(input)
  if (isNaN(d.getTime())) return '—'

  const { showTime = 'auto', withSeconds = true, timeZone } = options ?? {}

  // Heuristic: si la date a une heure non nulle, on affiche l’heure
  const hasTimeInfo =
    typeof input === 'string'
      ? /T\d{2}:\d{2}/.test(input)
      : !(d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0)

  const includeTime =
    showTime === 'always' || (showTime === 'auto' && hasTimeInfo)

  const datePart = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(d)

  if (!includeTime) return datePart

  const timePart = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(d)

  return `${datePart} ${timePart}` // évite les virgules/« à »
}

type XYPoint = { x: string | number; y: number | string | null | undefined }

export function sanitizeLinePoints(raw: XYPoint[] = []) {
  return raw
    .map((p) => {
      const x = p?.x
      const yNum =
        typeof p?.y === 'number'
          ? p.y
          : Number(String(p?.y ?? '').replace(',', '.'))

      const xOk =
        (typeof x === 'string' && x.trim().length > 0) ||
        (typeof x === 'number' && Number.isFinite(x))

      const yOk = Number.isFinite(yNum)

      if (!xOk || !yOk) return null
      return { x: typeof x === 'string' ? x.trim() : x, y: yNum }
    })
    .filter(Boolean) as Array<{ x: string | number; y: number }>
}

export const authKeys = {
  me: ['me'] as const,
}

export const usersKeys = {
  all: ['users'] as const,
  one: (id: string) => ['users', id] as const,
}

export const adminKeys = {
  ownersRoot: ['admin-owners'] as const,
  owners: (q: string, take: number) => ['admin-owners', q, take] as const,
  owner: (ownerId: string) => ['admin-owner', ownerId] as const,
}

export const ToLocaleStringFunc = (num: number | string) => {
  if (Number.isNaN(num)) {
    return Number(num).toLocaleString('fr-FR')
  }

  return Number(num).toLocaleString('fr-FR')
}
